import { castDraft, current, isDraft, original, WritableDraft } from 'immer';
import { deepEqual, DistributiveOmit, get, isObject, isPrimitive, Primitive } from '@direct-frontend/stdlib';
import { UnexpectedError } from '@adv-frontend/error';
import { CalculatedFieldStateProperties, FieldState, FormSliceState } from '../types';
import { extractObjectFieldStateFromUnion } from './extractObjectFieldStateFromUnion';
import { BaseFields } from '../../fields/types';
import { BaseValidationError } from '../../types';
import { getFieldPathById } from './getFieldPathByFieldId';
import { getFieldDeclarationById } from './getFieldDeclarationById';

/**
 * Функция, выставляющая в fieldState.statesByKey значение fieldState поля по ключу unionKeyValue
 */
export function setStateByKey({
    form,
    unionFieldId,
    unionKeyValue,
}: {
    form: WritableDraft<FormSliceState['forms'][string]>;
    unionFieldId: string;
    unionKeyValue: Primitive;
}) {
    const fieldState = form.fieldsState[unionFieldId];

    if (!fieldState || !('statesByKey' in fieldState)) {
        return;
    }

    if (typeof unionKeyValue === undefined || typeof unionKeyValue === null) {
        return;
    }

    const unionKeyValueString = String(unionKeyValue);

    const previousStatesByKey = isDraft(fieldState.statesByKey)
        ? original(fieldState.statesByKey)
        : fieldState.statesByKey;

    const objectFieldState = extractObjectFieldStateFromUnion(fieldState);

    const newStatesByKey = {
        ...previousStatesByKey,
        [unionKeyValueString]: objectFieldState,
    };

    if (!deepEqual(newStatesByKey, previousStatesByKey)) {
        fieldState.statesByKey = castDraft(newStatesByKey);
    }
}

/** Сохраняет stateByKey у всех родительских union полей */
export function setParentsStateByKey(props: {
    /** Значение формы из слайса */
    form: WritableDraft<FormSliceState['forms'][string]>;
    /** Поля формы из декларации */
    formFields: BaseFields;
    /** ID поля */
    fieldId: string;
}) {
    const { form, formFields, fieldId } = props;

    let currentFieldId: string | undefined = fieldId;

    while (currentFieldId) {
        const fieldState:
            | DistributiveOmit<FieldState<unknown, BaseValidationError>, CalculatedFieldStateProperties>
            | undefined = form.fieldsState[currentFieldId];

        if (fieldState && 'statesByKey' in fieldState) {
            const value = isDraft(form.value) ? current(form.value) : form.value;
            const path = getFieldPathById(() => form.fieldsState, currentFieldId);
            const currentValue = get(value, path);
            const fieldDeclaration = getFieldDeclarationById({
                fieldsState: form.fieldsState,
                formFields,
                fieldId: currentFieldId,
                // value с корня формы
                formValue: value,
            });

            if (fieldDeclaration?.type !== 'union') {
                console.warn(
                    new UnexpectedError(`По пути декларации не найдена декларация или не является union-полем`, {
                        cause: null,
                        additional: {
                            fieldDeclaration: JSON.stringify(fieldDeclaration),
                            currentFieldId,
                        },
                    })
                );

                continue;
            }

            const unionKey = fieldDeclaration.key;

            if (!isObject(currentValue) || unionKey === undefined) {
                continue;
            }

            const unionKeyValue = currentValue[unionKey];

            if (unionKeyValue === undefined || !isPrimitive(unionKeyValue)) {
                continue;
            }

            setStateByKey({
                form,
                unionFieldId: currentFieldId,
                unionKeyValue,
            });
        }

        currentFieldId = fieldState?.parentId;
    }
}
